import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { Trash2, Check } from "lucide-react";

// Компонент отдельной задачи

const TaskItem = ({ task, onToggle, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    setTimeout(() => onDelete(task.id), 300);
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 transition-all duration-300 hover:shadow-md group ${
        isDeleting ? "opacity-0 scale-95" : "opacity-100 scale-100"
      }`}
    >
      <button
        onClick={() => onToggle(task.id)}
        className={`flex-shrink-0 w-5 h-5 rounded border-2 transition-all duration-200 ${
          task.completed
            ? "bg-orange-400 border-orange-400"
            : "bg-white border-orange-300 hover:border-orange-400"
        }`}
        aria-label={
          task.completed
            ? "Отметить как невыполненную"
            : "Отметить как выполненную"
        }
      >
        {task.completed && <Check className="w-full h-full text-white p-0.5" />}
      </button>

      <span
        className={`flex-1 text-gray-800 transition-all duration-200 ${
          task.completed ? "line-through text-gray-400" : "text-gray-800"
        }`}
      >
        {task.text}
      </span>

      <button
        onClick={handleDelete}
        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all duration-200 opacity-0 group-hover:opacity-100"
        aria-label="Удалить задачу"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Компонент фильтрации задач

const Filter = ({ currentFilter, onFilterChange }) => {
  const filters = ["all", "active", "completed"];

  const filterLabels = {
    all: "Все",
    active: "Активные",
    completed: "Выполненные",
  };

  return (
    <div className="flex gap-2 justify-center mb-6">
      {filters.map((filter) => (
        <button
          key={filter}
          onClick={() => onFilterChange(filter)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            currentFilter === filter
              ? "bg-orange-400 text-white shadow-md transform scale-105"
              : "bg-white text-gray-600 hover:bg-orange-50 border border-gray-200 hover:border-orange-200"
          }`}
          aria-label={`Показать ${filterLabels[filter].toLowerCase()} задачи`}
          aria-pressed={currentFilter === filter}
        >
          {filterLabels[filter]}
        </button>
      ))}
    </div>
  );
};

// Основной компонент приложения Todo List

const App = () => {
  // Состояние для списка задач
  const [tasks, setTasks] = useState([]);

  // Состояние для поля ввода новой задачи
  const [inputValue, setInputValue] = useState("");

  // Состояние для текущего активного фильтра
  const [filter, setFilter] = useState("all");

  // Состояние для текста заметок
  const [notes, setNotes] = useState("");

  // Эффект для загрузки данных при монтировании компонента

  useEffect(() => {
    // Пример начальной задачи
    const initialTasks = [
      {
        id: 1,
        text: "Пример задачи - нажмите на чекбокс чтобы выполнить",
        completed: false,
      },
    ];
    setTasks(initialTasks);
  }, []);

  // Функция добавления новой задачи

  const addTask = () => {
    // Проверка на пустую строку
    if (inputValue.trim() === "") return;

    // Создание объекта новой задачи
    const newTask = {
      id: Date.now(),
      text: inputValue,
      completed: false,
    };

    // Добавление задачи в начало списка
    setTasks([newTask, ...tasks]);

    // Очистка поля ввода
    setInputValue("");
  };

  // Обработчик нажатия клавиши Enter в поле ввода

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      addTask();
    }
  };

  // Функция переключения состояния задачи (выполнено/не выполнено)

  const toggleTask = (id) => {
    setTasks(
      tasks.map((task) =>
        task.id === id ? { ...task, completed: !task.completed } : task
      )
    );
  };

  // Функция удаления задачи

  const deleteTask = (id) => {
    setTasks(tasks.filter((task) => task.id !== id));
  };

  // Фильтрация задач на основе выбранного фильтра

  const filteredTasks = tasks.filter((task) => {
    if (filter === "active") return !task.completed;
    if (filter === "completed") return task.completed;
    return true;
  });

  // Вычисление статистики задач

  const totalTasks = tasks.length;
  const activeTasks = tasks.filter((t) => !t.completed).length;
  const completedTasks = tasks.filter((t) => t.completed).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-orange-50 to-teal-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Контейнер с декоративной рамкой */}
        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Декоративный паттерн */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute top-10 left-10 w-20 h-20 bg-orange-500 rounded-full"></div>
            <div className="absolute top-20 right-16 w-16 h-16 bg-teal-500 rounded-full"></div>
            <div className="absolute bottom-32 left-20 w-24 h-24 bg-orange-500 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-20 h-20 bg-teal-500 rounded-full"></div>
            <div className="absolute top-44 left-32 w-20 h-20 bg-orange-500 rounded-full"></div>
            <div className="absolute top-32 right-60 w-16 h-16 bg-teal-500 rounded-full"></div>
            <div className="absolute bottom-12 left-80 w-24 h-24 bg-orange-500 rounded-full"></div>
            <div className="absolute bottom-40 right-16 w-20 h-20 bg-teal-500 rounded-full"></div>
          </div>

          {/* Основной контент */}
          <div className="relative z-10 p-8 sm:p-12">
            {/* Заголовок приложения */}
            <div className="text-center mb-8">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 tracking-tight mb-2">
                TO DO LIST
              </h1>
              <div className="w-24 h-1 bg-orange-400 mx-auto"></div>
            </div>

            {/* Секция задач */}
            <div className="mb-8">
              {/* Заголовок секции Задачи */}
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 px-4 bg-orange-400 flex rounded-md items-center">
                  <span className="text-white font-bold text-sm tracking-wider">
                    Задачи
                  </span>
                </div>
              </div>

              {/* Форма добавления задачи */}
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Добавить новую задачу..."
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors duration-200"
                    aria-label="Поле ввода новой задачи"
                  />
                  <button
                    onClick={addTask}
                    className="px-6 py-3 bg-orange-400 text-white rounded-lg hover:bg-orange-500 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
                    aria-label="Добавить задачу"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="hidden sm:inline">Добавить</span>
                  </button>
                </div>
              </div>

              {/* Компонент фильтров */}
              <Filter currentFilter={filter} onFilterChange={setFilter} />

              {/* Список задач */}
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {filteredTasks.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    {filter === "completed" && tasks.some((t) => !t.completed)
                      ? "Нет выполненных задач"
                      : filter === "active" && tasks.some((t) => t.completed)
                      ? "Нет активных задач"
                      : "Добавьте свою первую задачу!"}
                  </div>
                ) : (
                  filteredTasks.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onToggle={toggleTask}
                      onDelete={deleteTask}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Секция заметок */}
            <div>
              {/* Заголовок секции Заметки */}
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 px-4 rounded-md  bg-orange-400 flex items-center">
                  <span className="text-white font-bold text-sm tracking-wider">
                    Заметки
                  </span>
                </div>
              </div>

              {/* Текстовое поле для заметок */}
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Добавьте заметки здесь..."
                className="w-full h-32 px-4 py-3 bg-orange-50 border-2 border-orange-100 rounded-lg focus:outline-none focus:border-orange-400 transition-colors duration-200 resize-none"
                aria-label="Поле для заметок"
              />
            </div>

            {/* Секция статистики */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-around text-sm text-gray-600">
                <div className="text-center">
                  <div className="font-bold text-2xl text-orange-400">
                    {totalTasks}
                  </div>
                  <div>Всего</div>
                </div>

                <div className="text-center">
                  <div className="font-bold text-2xl text-teal-500">
                    {activeTasks}
                  </div>
                  <div>Активных</div>
                </div>

                <div className="text-center">
                  <div className="font-bold text-2xl text-green-500">
                    {completedTasks}
                  </div>
                  <div>Выполнено</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
